import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Heart, Search, Plus, TrendingUp, ImagePlus, X } from 'lucide-react';
import { toast } from 'sonner';
import { getCategories, getPosts, createPost } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); return `${d}d ago`;
}

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Community() {
  const { user, login } = useAuth();
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [active, setActive] = useState('all');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: '', title: '', content: '', image: null });
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const params = {};
    if (active !== 'all') params.category = active;
    if (q) params.q = q;
    setPosts(await getPosts(params));
  };

  useEffect(() => { getCategories().then(setCategories); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [active, q]);

  const openDialog = () => {
    if (!user) { toast.error('Please sign in to start a discussion.'); login(); return; }
    setOpen(true);
  };

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Image must be under 2MB.'); return; }
    const b64 = await fileToBase64(file);
    setForm((f) => ({ ...f, image: b64 }));
  };

  const submit = async () => {
    if (!form.title || !form.category || !form.content) {
      toast.error('Please fill in all fields.');
      return;
    }
    setLoading(true);
    try {
      await createPost(form);
      toast.success('Your discussion is live!');
      setOpen(false);
      setForm({ category: '', title: '', content: '', image: null });
      load();
    } catch (e) {
      if (e?.response?.status === 401) { toast.error('Please sign in first.'); login(); }
      else toast.error('Could not post. Try again.');
    } finally { setLoading(false); }
  };

  const catColor = (slug) => categories.find((c) => c.slug === slug)?.color || '#2E6BFF';
  const catName = (slug) => categories.find((c) => c.slug === slug)?.name || slug;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-12">
      <section className="bg-white rounded-[24px] p-8 sm:p-10 shadow-[0_25px_70px_-45px_rgba(14,23,38,0.4)] flex flex-col sm:flex-row justify-between gap-6">
        <div className="max-w-xl">
          <span className="eyebrow text-[#2E6BFF]">The community</span>
          <h1 className="font-display font-extrabold text-[#0E1726] text-[34px] sm:text-[44px] tracking-tight leading-[1.03] mt-3">
            Grow with ambitious product people.
          </h1>
          <p className="text-[#5B6472] text-[17px] leading-relaxed mt-4">
            Ask questions, share wins, and get real feedback on interviews, strategy, AI, and leadership from a community that's been there.
          </p>
        </div>
        <button onClick={openDialog} className="self-start bg-[#2E6BFF] text-white font-semibold px-6 py-3.5 rounded-full inline-flex items-center gap-2 hover:bg-[#1f5aef] transition-colors whitespace-nowrap">
          <Plus size={18} /> Start a discussion
        </button>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-[22px]">Start a discussion</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <label className="text-sm font-semibold">Category</label>
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {categories.map((c) => (
                    <button
                      type="button"
                      key={c.slug}
                      onClick={() => setForm({ ...form, category: c.slug })}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                        form.category === c.slug ? 'text-white border-transparent' : 'text-[#0E1726] border-black/15 hover:bg-black/5'
                      }`}
                      style={form.category === c.slug ? { background: c.color } : {}}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-semibold">Title</label>
                <Input className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="What's on your mind?" />
              </div>
              <div>
                <label className="text-sm font-semibold">Details</label>
                <Textarea className="mt-1.5 min-h-[120px]" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Share the context... Use **bold** and @mention people." />
                <p className="text-xs text-[#5B6472] mt-1.5">Tip: **text** for bold, @name to mention someone.</p>
              </div>
              {form.image ? (
                <div className="relative inline-block">
                  <img src={form.image} alt="preview" className="max-h-40 rounded-lg border border-black/10" />
                  <button onClick={() => setForm({ ...form, image: null })} className="absolute -top-2 -right-2 bg-[#0E1B2E] text-white rounded-full w-6 h-6 flex items-center justify-center"><X size={14} /></button>
                </div>
              ) : (
                <button type="button" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 text-sm font-semibold text-[#2E6BFF]">
                  <ImagePlus size={16} /> Add an image
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickImage} />
              <button onClick={submit} disabled={loading} className="w-full bg-[#0E1B2E] text-white font-semibold py-3 rounded-full hover:bg-[#1b2a44] transition-colors disabled:opacity-60">
                {loading ? 'Posting...' : 'Post discussion'}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </section>

      <section className="mt-8 flex flex-col lg:flex-row gap-8">
        <aside className="lg:w-56 shrink-0">
          <div className="relative mb-5">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5B6472]" />
            <Input className="pl-9" placeholder="Search discussions" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="eyebrow text-[#5B6472] mb-3">Categories</div>
          <div className="flex lg:flex-col gap-2 flex-wrap">
            <button onClick={() => setActive('all')} className={`text-left px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${active === 'all' ? 'bg-[#0E1B2E] text-white' : 'text-[#0E1726] hover:bg-white'}`}>All topics</button>
            {categories.map((c) => (
              <button key={c.slug} onClick={() => setActive(c.slug)} className={`text-left px-3.5 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${active === c.slug ? 'bg-[#0E1B2E] text-white' : 'text-[#0E1726] hover:bg-white'}`}>
                <span className="w-2 h-2 rounded-full" style={{ background: c.color }} /> {c.name}
              </button>
            ))}
          </div>
        </aside>

        <div className="flex-1">
          {posts.length === 0 && (
            <div className="bg-white rounded-2xl p-10 text-center text-[#5B6472]">No discussions yet. Be the first to start one!</div>
          )}
          <div className="space-y-4">
            {posts.map((p) => (
              <Link key={p.id} to={`/community/${p.id}`} className="block bg-white rounded-2xl p-6 shadow-[0_20px_50px_-42px_rgba(14,23,38,0.4)] hover:-translate-y-0.5 transition-transform">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: `${catColor(p.category)}1a`, color: catColor(p.category) }}>{catName(p.category)}</span>
                  {p.pinned && <span className="text-xs font-semibold text-[#F59E0B] flex items-center gap-1"><TrendingUp size={13} /> Featured</span>}
                  <span className="text-xs text-[#5B6472]">{timeAgo(p.created_at)}</span>
                </div>
                <h3 className="font-display font-bold text-[#0E1726] text-[19px] mt-3">{p.title}</h3>
                <p className="text-[#5B6472] mt-1.5 line-clamp-2">{p.content}</p>
                <div className="flex items-center gap-5 mt-4 text-sm text-[#5B6472]">
                  <span className="font-medium text-[#0E1726]">{p.author}</span>
                  <span className="flex items-center gap-1.5"><MessageSquare size={15} /> {p.reply_count}</span>
                  <span className="flex items-center gap-1.5"><Heart size={15} /> {p.likes}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
